/*
  The target-org blueprint: the organisation you are designing, as opposed to the one you have.

  Three things make this the subtlest group in the catalogue.

  1. A unit's IDENTITY IS ITS PATH. There are no unit ids — a unit is `['Commercial', 'Sales']`, and the
     designer works top-down, so a unit exists on the chart before anybody is placed into it. That means
     renaming and re-parenting are path rewrites, and two rewrites can collide into the same path, which
     is not an error: it is a MERGE, and the original relies on it (dedupeTargetUnits, orgchart.html:4449
     and :4486).

  2. A RENAME IS BY VALUE, NOT BY NODE. renameTargetValue (:4446) rewrites that value wherever it
     appears at that level, so two unrelated units both called "Ops" under different parents are renamed
     together. Modelling units as nodes with parent references would quietly produce different
     behaviour, so this follows the original.

  3. THE UNITS ARE PROJECT-SCOPED, THE PLACEMENTS ARE NOT. The blueprint belongs to the deal; who you
     have put where is a per-scenario decision (a deliberate divergence — the shipped app shares one set
     across every scenario, so comparing two carve-out options cannot compare their target orgs). So a
     path rewrite has to repath placements in every scenario, which is why these handlers read
     targetPlacements across all of them.

  reparentTargetUnit's order of operations is load-bearing and documented on the handler.
*/
import { MAX_TARGET_LEVELS } from '@integrate/contracts';
import { CommandError } from '../errors.js';
import { required } from './types.js';
/** Target levels are keyed tlvl1..tlvlN, mirroring lvl1..lvlN for the as-is hierarchy. */
export function targetLevelKey(index) {
    return `tlvl${index + 1}`;
}
function levelKeys(levels) {
    return levels.map((l) => l.key);
}
/** Units are compared and deduped by their path, since the path is the identity. */
function pathKey(path) {
    return JSON.stringify(path);
}
/**
 * Drop duplicate paths, keeping first-seen order.
 *
 * This is the merge mechanism, not hygiene: a rename onto an existing sibling and a re-parent onto an
 * occupied slot both converge two paths into one, and the original's UI advertises that (:4543).
 */
function dedupeUnits(units) {
    const seen = new Set();
    const out = [];
    for (const unit of units) {
        const key = pathKey(unit);
        if (seen.has(key))
            continue;
        seen.add(key);
        out.push([...unit]);
    }
    return out;
}
function isPrefix(prefix, path) {
    if (prefix.length > path.length)
        return false;
    return prefix.every((value, i) => path[i] === value);
}
/**
 * A person's placement as a path.
 *
 * Stops at the first level they have no value for, matching targetPlacementValues (:4432): a placement
 * is a contiguous path from the top, and a value below a gap describes a unit nobody can see. The write
 * side refuses to create such a gap, which is a small divergence from the original — there the write
 * skipped blanks while the read stopped at them, so the two disagreed about what was stored.
 */
export function placementPath(byLevel, keys) {
    const out = [];
    if (!byLevel)
        return out;
    for (const key of keys) {
        const value = byLevel[key];
        if (value === undefined || String(value).trim() === '')
            break;
        out.push(String(value));
    }
    return out;
}
/** Every (scenario, employee) placement in the project, as paths. */
function allPlacements(slice, keys) {
    const out = [];
    for (const [scenarioId, byPerson] of Object.entries(required(slice, 'targetPlacements'))) {
        for (const [employeeId, byLevel] of Object.entries(byPerson)) {
            const path = placementPath(byLevel, keys);
            if (path.length)
                out.push({ scenarioId, employeeId, path });
        }
    }
    return out;
}
/**
 * The writes that replace one person's placement wholesale.
 *
 * Delete-then-rebuild, never merge (:4441). A merge-based implementation leaves stale deeper levels
 * behind, so moving somebody from a three-level unit to a two-level one would keep them nominally in
 * their old sub-team.
 */
function placementWrites(scenarioId, employeeId, path, keys) {
    const writes = keys.map((fieldKey) => ({
        t: 'override',
        scenarioId,
        perspective: 'target',
        employeeId,
        fieldKey,
        value: undefined,
    }));
    path.slice(0, keys.length).forEach((value, i) => {
        writes.push({
            t: 'override',
            scenarioId,
            perspective: 'target',
            employeeId,
            fieldKey: keys[i],
            value,
        });
    });
    return writes;
}
/* ------------------------------------------------------------------ *
 * The ladder
 * ------------------------------------------------------------------ */
/** Three levels, matching seedTargetLevels (orgchart.html:1986). */
function seededLevels() {
    return [
        { key: 'tlvl1', label: 'L1' },
        { key: 'tlvl2', label: 'L2' },
        { key: 'tlvl3', label: 'L3' },
    ];
}
export const setTargetEnabled = {
    needs: ['settings'],
    apply(slice, command) {
        const settings = required(slice, 'settings');
        if (settings.targetEnabled === command.enabled) {
            throw new CommandError('conflict', command.enabled ? 'The target design is already on' : 'The target design is already off');
        }
        // Turning it on with no ladder gives you nothing to design into, so seed three levels — the same
        // thing the original does, rather than leaving the designer to invent them first.
        const seeding = command.enabled && settings.targetLevels.length === 0;
        return {
            writes: [
                {
                    t: 'settings',
                    patch: { targetEnabled: command.enabled, ...(seeding ? { targetLevels: seededLevels() } : {}) },
                },
            ],
            /*
              Turning it off keeps the design: the units, the ladder and every placement stay, so switching
              it back on returns to where you were rather than to a blank sheet.
      
              The seeded ladder survives an undo, deliberately. Emptying it again would need a command that
              clears the ladder, and that is exactly the destructive shortening branch `setTargetLevels` now
              refuses to express — so the choice is a documented asymmetry or a command whose whole purpose is
              to be dangerous. The asymmetry is benign: three empty levels with the design switched off change
              no figure, render nothing, and are what the next enable would produce anyway.
            */
            inverse: [{ type: 'setTargetEnabled', enabled: settings.targetEnabled }],
            summary: command.enabled
                ? `Enabled the target org design${seeding ? ' (seeded three levels)' : ''}`
                : 'Disabled the target org design',
            touched: ['target:enabled'],
        };
    },
};
export const setTargetLevels = {
    needs: ['settings', 'targetPlacements'],
    apply(slice, command) {
        const settings = required(slice, 'settings');
        const before = settings.targetLevels;
        // Keys are positional — tlvl3 is the third level — so the ladder cannot be reordered or
        // arbitrarily keyed without changing what every stored placement means.
        command.levels.forEach((level, i) => {
            const expected = targetLevelKey(i);
            if (level.key !== expected) {
                throw new CommandError('invalid', `Target level ${i + 1} must be keyed ${expected}; the keys are positional`);
            }
        });
        const beforeKeys = levelKeys(before);
        const afterKeys = levelKeys(command.levels);
        const removedKeys = beforeKeys.filter((k) => !afterKeys.includes(k));
        /*
          Only the TRAILING levels may go, and the LABELS are the only way to tell.
    
          Keys are positional, so a three-level ladder with the middle level dropped arrives keyed
          tlvl1, tlvl2 — identical to a ladder that lost its last level. The labels are what distinguishes
          them: a trailing removal leaves every survivor's label where it was, while dropping the middle one
          shifts "L3" up into position 2. The stored values do NOT shift with it, so every `tlvl2` value
          keeps meaning what it did while the real third-tier values are deleted, and the whole design
          silently moves up a tier.
    
          The original only ever offers ✕ on the last level (:5947), which is the only shape positional
          keying can express honestly. A relabel combined with a removal is refused for the same reason —
          the two are indistinguishable here — so they are done one at a time.
        */
        if (removedKeys.length && !command.levels.every((level, i) => before[i]?.label === level.label)) {
            throw new CommandError('invalid', 'Target levels can only be removed from the end, and not renamed in the same change — the keys ' +
                'are positional, so dropping a level from the middle would move every value below it up a tier');
        }
        const writes = [];
        // Shortening the ladder truncates every unit path to the new depth and drops what becomes empty,
        // then dedupes — because two units that differed only below the removed level are now the same one.
        let units = settings.targetUnits;
        if (removedKeys.length) {
            units = dedupeUnits(settings.targetUnits.map((u) => u.slice(0, afterKeys.length)).filter((u) => u.length > 0));
            // And every placement loses its values at the removed levels. The original deletes the keys but
            // leaves the now-empty per-person object behind; there is no such object here to leak.
            for (const [scenarioId, byPerson] of Object.entries(required(slice, 'targetPlacements'))) {
                for (const employeeId of Object.keys(byPerson)) {
                    for (const fieldKey of removedKeys) {
                        writes.push({
                            t: 'override',
                            scenarioId,
                            perspective: 'target',
                            employeeId,
                            fieldKey,
                            value: undefined,
                        });
                    }
                }
            }
        }
        writes.push({ t: 'settings', patch: { targetLevels: command.levels, targetUnits: units } });
        return {
            writes,
            // Removing a level discards the placements below it, which the ladder alone cannot restore, so
            // shortening is honestly irreversible. Renaming or extending is not.
            inverse: removedKeys.length ? null : [{ type: 'setTargetLevels', levels: before }],
            summary: `Target levels: ${command.levels.map((l) => l.label).join(' › ') || '(none)'}` +
                (removedKeys.length
                    ? ` — removed ${removedKeys.length} level${removedKeys.length === 1 ? '' : 's'} and the placements below`
                    : ''),
            touched: ['target:levels'],
        };
    },
};
/* ------------------------------------------------------------------ *
 * Units
 * ------------------------------------------------------------------ */
export const addTargetUnit = {
    needs: ['settings'],
    apply(slice, command) {
        const settings = required(slice, 'settings');
        assertNoBlanks(command.path);
        if (command.path.length > settings.targetLevels.length) {
            throw new CommandError('conflict', `The ladder has ${settings.targetLevels.length} level(s); that path needs ${command.path.length}`);
        }
        if (settings.targetUnits.some((u) => pathKey(u) === pathKey(command.path))) {
            throw new CommandError('conflict', `Target unit "${command.path.join(' › ')}" already exists`);
        }
        // Declaring a nested unit declares its ancestors, so the boxes above it exist to render into.
        const ancestors = [];
        for (let i = 1; i < command.path.length; i++) {
            const prefix = command.path.slice(0, i);
            if (!settings.targetUnits.some((u) => pathKey(u) === pathKey(prefix)))
                ancestors.push(prefix);
        }
        return {
            writes: [
                {
                    t: 'settings',
                    patch: {
                        targetUnits: dedupeUnits([...settings.targetUnits, ...ancestors, [...command.path]]),
                    },
                },
            ],
            /*
              The minted ancestors come off too, deepest first.
      
              `removeTargetUnit(path)` filters by `isPrefix(path, u)`, which never matches a unit ABOVE the
              named one — so an inverse naming only the path left every parent this command created standing.
              Adding `A › B › C` to an empty design and undoing it left `A` and `B` behind: two phantom boxes
              on the canvas and two phantom zero rows in the rollup and the exported sheet, after an undo that
              reported success.
            */
            inverse: [
                { type: 'removeTargetUnit', path: command.path },
                ...[...ancestors].reverse().map((path) => ({ type: 'removeTargetUnit', path })),
            ],
            summary: `Added target unit "${command.path.join(' › ')}"` +
                (ancestors.length ? ` (and ${ancestors.length} parent unit(s))` : ''),
            touched: [`target:unit:${pathKey(command.path)}`],
        };
    },
};
/**
 * Declare many units at once, without caring which already exist.
 *
 * The gesture behind it is "recreate this team's sub-teams in the blueprint" (orgchart.html:4638),
 * where the paths come from a real group's shape rather than from somebody typing them — so how many
 * are already declared is not a fact the caller has or should assert. Declaring an existing path is
 * therefore fine and simply changes nothing.
 *
 * Ancestors are minted the same way `addTargetUnit` mints them, and the inverse removes ONLY what this
 * command actually added, deepest first. That is the difference that matters on a shared project: two
 * people mirroring overlapping teams must not be able to undo each other's units.
 */
export const declareTargetUnits = {
    needs: ['settings'],
    apply(slice, command) {
        const settings = required(slice, 'settings');
        const depth = settings.targetLevels.length;
        const existing = new Set(settings.targetUnits.map((u) => pathKey(u)));
        const minted = [];
        const seen = new Set();
        for (const path of command.paths) {
            assertNoBlanks(path);
            if (path.length > depth) {
                throw new CommandError('conflict', `The ladder has ${depth} level(s); "${path.join(' › ')}" needs ${path.length}`);
            }
            // Every prefix, so the boxes above a nested unit exist to render into.
            for (let i = 1; i <= path.length; i++) {
                const prefix = path.slice(0, i);
                const key = pathKey(prefix);
                if (existing.has(key) || seen.has(key))
                    continue;
                seen.add(key);
                minted.push(prefix);
            }
        }
        if (!minted.length) {
            return {
                writes: [],
                inverse: [],
                summary: `Target units unchanged — all ${command.paths.length} already declared`,
                touched: [],
            };
        }
        return {
            writes: [
                {
                    t: 'settings',
                    patch: { targetUnits: dedupeUnits([...settings.targetUnits, ...minted]) },
                },
            ],
            // Deepest first, so each removal takes a branch that is already empty below it.
            inverse: [...minted]
                .sort((a, b) => b.length - a.length)
                .map((path) => ({ type: 'removeTargetUnit', path })),
            summary: `Declared ${minted.length} target unit${minted.length === 1 ? '' : 's'}`,
            touched: minted.map((path) => `target:unit:${pathKey(path)}`),
        };
    },
};
export const removeTargetUnit = {
    needs: ['settings', 'targetPlacements'],
    apply(slice, command) {
        const settings = required(slice, 'settings');
        const keys = levelKeys(settings.targetLevels);
        if (!settings.targetUnits.some((u) => pathKey(u) === pathKey(command.path))) {
            throw new CommandError('not_found', `No target unit "${command.path.join(' › ')}"`);
        }
        // The whole branch goes, not just the named unit.
        const removed = settings.targetUnits.filter((u) => isPrefix(command.path, u));
        const kept = settings.targetUnits.filter((u) => !isPrefix(command.path, u));
        const writes = [
            { t: 'settings', patch: { targetUnits: kept } },
        ];
        /*
          Anyone placed inside the branch moves up to its parent rather than vanishing from the target org:
          the unit was removed, not the person, and truncating is what the original does (:4537).
    
          Except at the top level, where there IS no parent. `placementWrites(…, [], keys)` emits a delete
          per level and writes nothing back, so those people leave the target org entirely — which is
          correct, and is what the app does too. The summary has to say so: it read "moving N placement(s)
          up" in both cases, which is a permanent history entry claiming the opposite of what happened.
        */
        const parent = command.path.slice(0, -1);
        let moved = 0;
        for (const placement of allPlacements(slice, keys)) {
            if (!isPrefix(command.path, placement.path))
                continue;
            writes.push(...placementWrites(placement.scenarioId, placement.employeeId, parent, keys));
            moved++;
        }
        const movedText = !moved
            ? ''
            : parent.length
                ? `, moving ${moved} placement(s) up to "${parent.join(' › ')}"`
                : `, removing ${moved} placement(s) from the target org`;
        return {
            writes,
            inverse: null,
            summary: `Removed target unit "${command.path.join(' › ')}"` +
                (removed.length > 1 ? ` and ${removed.length - 1} sub-unit(s)` : '') +
                movedText,
            touched: [`target:unit:${pathKey(command.path)}`],
        };
    },
};
export const renameTargetValue = {
    needs: ['settings', 'targetPlacements'],
    apply(slice, command) {
        const settings = required(slice, 'settings');
        const keys = levelKeys(settings.targetLevels);
        const key = keys[command.levelIndex];
        if (!key) {
            throw new CommandError('not_found', `The ladder has no level ${command.levelIndex + 1}`);
        }
        assertNoBlanks([command.newValue]);
        const rewrite = (path) => path.map((value, i) => (i === command.levelIndex && value === command.oldValue ? command.newValue : value));
        const nextUnits = dedupeUnits(settings.targetUnits.map(rewrite));
        const merged = settings.targetUnits.length - nextUnits.length;
        const writes = [{ t: 'settings', patch: { targetUnits: nextUnits } }];
        // By value and not by node: every unit carrying this value at this level is renamed, wherever it
        // sits in the tree. Placements follow, in every scenario.
        let touchedPlacements = 0;
        for (const placement of allPlacements(slice, keys)) {
            const next = rewrite(placement.path);
            if (pathKey(next) === pathKey(placement.path))
                continue;
            writes.push(...placementWrites(placement.scenarioId, placement.employeeId, next, keys));
            touchedPlacements++;
        }
        return {
            writes,
            // Reversible only when nothing merged: once two units have become one, the rename cannot say
            // which people belonged to which.
            inverse: merged
                ? null
                : [
                    {
                        type: 'renameTargetValue',
                        levelIndex: command.levelIndex,
                        oldValue: command.newValue,
                        newValue: command.oldValue,
                    },
                ],
            summary: `Renamed target unit "${command.oldValue}" → "${command.newValue}" at ${settings.targetLevels[command.levelIndex].label}` +
                (merged ? ` — merged into ${merged} existing unit(s)` : '') +
                (touchedPlacements ? `, ${touchedPlacements} placement(s) followed` : ''),
            touched: [`target:value:${key}:${command.oldValue}`],
        };
    },
};
/**
 * Move a unit and its whole subtree under a new parent. An empty `toParent` promotes it to the top.
 *
 * The order of operations is the port, and every step earns its place:
 *
 *   1. refuse a move into the unit's own branch, or the tree stops being a tree;
 *   2. compute the deepest repathed result and GROW THE LADDER FIRST — a subtree moved deeper than the
 *      ladder allows would otherwise have its tail silently truncated by the level cap;
 *   3. repath the units in the branch;
 *   4. declare the destination's implicit ancestors, so moving under a unit that was only implied makes
 *      it real and the boxes above render;
 *   5. dedupe, which is how a move onto an occupied slot merges;
 *   6. repath the placements, in every scenario.
 *
 * Doing (2) after (3) is the mistake that loses the deep end of a moved subtree, and it loses it
 * silently — the paths are simply shorter than they were.
 */
export const reparentTargetUnit = {
    needs: ['settings', 'targetPlacements'],
    apply(slice, command) {
        const settings = required(slice, 'settings');
        const source = command.path;
        const destination = command.toParent;
        if (source.length === 0)
            throw new CommandError('invalid', 'No unit named');
        assertNoBlanks(source);
        assertNoBlanks(destination);
        if (!settings.targetUnits.some((u) => pathKey(u) === pathKey(source))) {
            throw new CommandError('not_found', `No target unit "${source.join(' › ')}"`);
        }
        // (1) A unit cannot become its own descendant.
        if (isPrefix(source, destination)) {
            throw new CommandError('conflict', 'A unit cannot be moved inside its own branch');
        }
        if (pathKey(source.slice(0, -1)) === pathKey(destination)) {
            throw new CommandError('conflict', 'That unit is already there');
        }
        const leaf = source[source.length - 1];
        const repath = (path) => [
            ...destination,
            leaf,
            ...path.slice(source.length),
        ];
        const inBranchUnits = settings.targetUnits.filter((u) => isPrefix(source, u));
        const keysBefore = levelKeys(settings.targetLevels);
        const inBranchPlacements = allPlacements(slice, keysBefore).filter((p) => isPrefix(source, p.path));
        // (2) Grow the ladder to fit the deepest repathed result, BEFORE anything is written.
        const deepest = Math.max(...inBranchUnits.map((u) => repath(u).length), ...inBranchPlacements.map((p) => repath(p.path).length), settings.targetLevels.length);
        /*
          The ladder has a ceiling, and it is the contract's.
    
          `setTargetLevels` accepts at most 12 levels, `targetUnitSchema` at most 12 segments, and
          `renameTargetValue.levelIndex` at most 11. Growing past that here produced units nothing could
          then remove, rename or place into, and the only ladder `setTargetLevels` would accept was one
          that takes the destructive truncation branch. Refused before any write, like the depth check on
          `addTargetUnit`.
        */
        if (deepest > MAX_TARGET_LEVELS) {
            throw new CommandError('conflict', `That move would need ${deepest} levels; the target ladder holds at most ${MAX_TARGET_LEVELS}`);
        }
        const levels = [...settings.targetLevels];
        while (levels.length < deepest) {
            levels.push({ key: targetLevelKey(levels.length), label: `L${levels.length + 1}` });
        }
        const keys = levelKeys(levels);
        // (3) Repath the branch. (4) Declare the destination's implicit ancestors. (5) Dedupe.
        const moved = settings.targetUnits.map((u) => (isPrefix(source, u) ? repath(u) : u));
        /*
          Only the ancestors that are NOT already declared, which is what `addTargetUnit` does.
    
          Pushing every prefix unconditionally meant the ordinary gesture — moving a unit under one that
          already exists — put the destination in both lists, so dedupe removed one and the arithmetic
          below read it as a merge. The consequence was not cosmetic: a merge nulls the inverse, so the
          primary target gesture was permanently unrevertable, and the history told the user units had been
          merged and people mixed when nothing of the sort happened. Being told that on every move is how a
          user learns to ignore the message on the one operation where it is true.
        */
        const ancestors = [];
        for (let i = 1; i <= destination.length; i++) {
            const prefix = destination.slice(0, i);
            if (!moved.some((u) => pathKey(u) === pathKey(prefix)))
                ancestors.push(prefix);
        }
        const nextUnits = dedupeUnits([...moved, ...ancestors]);
        const writes = [
            {
                t: 'settings',
                patch: {
                    targetUnits: nextUnits,
                    ...(levels.length !== settings.targetLevels.length ? { targetLevels: levels } : {}),
                },
            },
        ];
        // (6) Repath the placements, across every scenario.
        for (const placement of inBranchPlacements) {
            writes.push(...placementWrites(placement.scenarioId, placement.employeeId, repath(placement.path), keys));
        }
        const grew = levels.length - settings.targetLevels.length;
        /*
          A merge is two units of the MOVED set collapsing into one, which is the only case that loses
          information about who was where. Ancestors are excluded from the count because they are
          declarations, not collisions — see the comment where they are built.
        */
        const mergedInto = moved.length - dedupeUnits(moved).length;
        return {
            writes,
            /*
              A move that merged, that grew the ladder, or that had to declare a new parent cannot be undone
              by moving back: the merge lost which people were in which unit, and the extra levels and the
              minted parents would need removing too. Anything else round-trips.
            */
            inverse: mergedInto || grew || ancestors.length
                ? null
                : [
                    {
                        type: 'reparentTargetUnit',
                        path: [...destination, leaf],
                        toParent: source.slice(0, -1),
                    },
                ],
            summary: `Moved target unit "${source.join(' › ')}" under "${destination.join(' › ') || 'the top level'}"` +
                (inBranchUnits.length > 1 ? ` with ${inBranchUnits.length - 1} sub-unit(s)` : '') +
                (inBranchPlacements.length ? `, ${inBranchPlacements.length} placement(s) followed` : '') +
                (grew ? `, ladder grown by ${grew} level(s)` : '') +
                (mergedInto ? `, merged into ${mergedInto} existing unit(s)` : ''),
            touched: [`target:unit:${pathKey(source)}`],
        };
    },
};
/* ------------------------------------------------------------------ *
 * Placement
 * ------------------------------------------------------------------ */
export const setTargetPlacement = {
    needs: ['employees', 'buildUps', 'settings', 'overrides'],
    apply(slice, command, ctx) {
        const settings = required(slice, 'settings');
        const keys = levelKeys(settings.targetLevels);
        const existing = required(slice, 'overrides').target ?? {};
        if (!settings.targetEnabled) {
            throw new CommandError('conflict', 'The target org design is not enabled for this project');
        }
        const writes = [];
        const touched = [];
        const restore = [];
        for (const assignment of command.assignments) {
            // A planned hire can be placed in the target org just like anybody else.
            const known = required(slice, 'employees')[assignment.employeeId] ??
                required(slice, 'buildUps')[assignment.employeeId];
            if (!known) {
                throw new CommandError('not_found', `No such person or build-up: ${assignment.employeeId}`);
            }
            if (assignment.path !== null) {
                assertNoBlanks(assignment.path);
                if (assignment.path.length > keys.length) {
                    throw new CommandError('conflict', `The ladder has ${keys.length} level(s); that placement needs ${assignment.path.length}`);
                }
                if (!settings.targetUnits.some((u) => pathKey(u) === pathKey(assignment.path))) {
                    throw new CommandError('not_found', `No target unit "${assignment.path.join(' › ')}" — declare it first`);
                }
            }
            const previous = placementPath(existing[assignment.employeeId], keys);
            writes.push(...placementWrites(ctx.scenarioId, assignment.employeeId, assignment.path ?? [], keys));
            touched.push(`target:placement:${assignment.employeeId}`);
            restore.push({ employeeId: assignment.employeeId, path: previous.length ? previous : null });
        }
        const n = command.assignments.length;
        const first = command.assignments[0];
        return {
            writes,
            inverse: [
                { type: 'setTargetPlacement', scenarioId: command.scenarioId, assignments: restore },
            ],
            summary: first.path === null
                ? `Removed ${n} ${n === 1 ? 'person' : 'people'} from the target org`
                : `Placed ${n} ${n === 1 ? 'person' : 'people'} in "${first.path.join(' › ')}"`,
            touched,
        };
    },
};
/**
 * A path segment may not be blank.
 *
 * The original's placement write skipped blank values while its read stopped at the first one, so the
 * two disagreed about what was stored and a gap silently truncated the path on the way back out.
 * Refusing the gap makes write and read agree, and makes a placement round-trip exactly.
 */
function assertNoBlanks(path) {
    for (const segment of path) {
        if (String(segment).trim() === '') {
            throw new CommandError('invalid', 'A target unit path may not contain a blank level');
        }
    }
}
//# sourceMappingURL=target.js.map